import React from "react";
import { Loader2, AlertCircle, ExternalLink } from "lucide-react";

/**
 * Redirect-in-progress visual for hosted-checkout flows (CinetPay).
 * Three states:
 *   - "idle"     : not started (component returns null)
 *   - "checking" : redirect being prepared / waiting for CinetPay URL
 *   - "ok"       : redirecting now (full opacity, message)
 *   - "error"    : init failed
 */
export default function RedirectingOverlay({ status = "idle", message = "", brand = "CinetPay" }) {
  if (status === "idle") return null;
  const isError = status === "error";

  return (
    <div
      className="glass rounded-2xl p-8 flex flex-col items-center text-center"
      data-testid="redirecting-overlay"
      data-status={status}
    >
      <div className="relative w-20 h-20 mb-5 flex items-center justify-center">
        {!isError && (
          <>
            {/* Outer ripple rings */}
            <span className="absolute inset-0 rounded-full border-2 border-cyan-400/40 animate-ping" />
            <span
              className="absolute inset-2 rounded-full border-2 border-cyan-400/30 animate-ping"
              style={{ animationDelay: "300ms" }}
            />
          </>
        )}
        <div
          className={`relative w-14 h-14 rounded-full flex items-center justify-center ${
            isError
              ? "bg-red-400/15 border border-red-400/40 text-red-300"
              : "bg-cyan-400/15 border border-cyan-400/40 text-cyan-300"
          }`}
        >
          {isError ? (
            <AlertCircle className="h-6 w-6" />
          ) : (
            <Loader2 className="h-7 w-7 animate-spin" />
          )}
        </div>
      </div>

      <div className="text-sm font-semibold mb-1.5">
        {isError
          ? "Échec d'initialisation"
          : status === "ok"
          ? `Redirection vers ${brand}…`
          : `Initialisation du paiement ${brand}…`}
      </div>

      {!isError && (
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5 mb-3">
          <ExternalLink className="h-3 w-3" />
          Page de paiement sécurisée
        </div>
      )}

      {message && (
        <div className={`text-xs leading-relaxed max-w-md ${isError ? "text-red-300" : "text-zinc-400"}`}>
          {message}
        </div>
      )}

      {!isError && status === "checking" && (
        <div className="mt-5 w-full max-w-xs h-1 rounded-full overflow-hidden bg-white/[0.05]">
          <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-[slide_1.4s_ease-in-out_infinite]" />
        </div>
      )}

      <style>{`
        @keyframes slide {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
}
